import React from "react";
import Button from "../reusable/Button";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { LazyMotion, domAnimation, m } from "framer-motion";

export interface BeforeAfterProps {
  data: BeforeAfterData;
}
export interface BeforeAfterData {
  title: string;
  subtitle: string;
  galleryLabel: string;
  buttons: any[];
  beforeImage: string;
  afterImage: string;
  before_image_url: string;
  after_image_url: string;
  alt: string;
  sectionName?: string;
}

// Fade-in varijanta za motion
const fadeIn = {
  hidden: { opacity: 0 },
  animate: { opacity: 1 },
  visible: { opacity: 1, transition: { duration: 1, ease: "easeOut" } },
};

export default function BeforeAfter({ data }: BeforeAfterProps) {
  const sectionId = data.sectionName && data.sectionName.replace(/\s+/g, "-");

  const useCloudinary = data.before_image_url && data.after_image_url;
  const beforeImageSrc = useCloudinary
    ? data.before_image_url
    : getIconUrl(data.beforeImage);
  const afterImageSrc = useCloudinary
    ? data.after_image_url
    : getIconUrl(data.afterImage);
  const altImage = data.alt || "Before and After Image";

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      data-name={data.sectionName}
      className="w-full bg-accent-color-three py-8 flex justify-center desktop:py-16"
    >
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "85vw",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {/* Mobile/tablet layout */}
        <div className="flex flex-col desktop:hidden">
              <m.div
                className="text-h2-tablet text-accent-color-two font-serif mb-2"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                {data.title}
              </m.div>
              <m.p
                className="text-special-h2 text-secondary-light uppercase"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                {data.subtitle}
              </m.p>
          <div className="flex flex-row items-center justify-center gap-8 w-full mb-8">
            {[beforeImageSrc, afterImageSrc].map((img, idx) => (
              <m.div
                key={idx}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
              >
                <Image
                  src={img}
                  alt={idx === 0 ? "Before" : "After"}
                  width={320}
                  height={380}
                  className="font-sans object-contain mb-2 w-[320px] h-[380px] rounded-none"
                />
                <span className="text-accent-color-two text-md tracking-widest mt-2">
                  {idx === 0 ? "BEFORE" : "AFTER"}
                </span>
              </m.div>
            ))}
          </div>
          <div className="flex flex-col justify-between">
            <div>
              <m.div
                className="font-sans text-paragraph-large text-white mb-12"
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                {data.galleryLabel}
              </m.div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 justify-items-center desktop:m-auto w-full max-w-[700px]">
                {data.buttons.map((btn, i) => (
                  <m.div
                    key={btn.item.title}
                    variants={fadeIn}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    <Button
                      title={btn.item.title}
                      type={btn.item.type}
                      link={btn.item.link}
                      className="w-[320px] h-[60px] flex items-center justify-center"
                    />
                  </m.div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Desktop layout */}
        <div
          className="hidden desktop:flex flex-row items-end"
          style={{
            width: "100%",
            maxWidth: "85vw",
            marginLeft: "auto",
            marginRight: "auto",
            gap: "5%",
          }}
        >
          {/* Text Section - 40% */}
          <div
            className="flex flex-col justify-center"
            style={{
              flexBasis: "40%",
              flexGrow: 0,
              flexShrink: 0,
              maxWidth: "40%",
            }}
          >
            <m.h2
              className="text-h2-tablet desktop:text-h2-desktop text-accent-color-two font-serif mb-2"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {data.title}
            </m.h2>
            <m.p
              className="text-special-h2 mb-12 text-secondary-light uppercase"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {data.subtitle}
            </m.p>
            <m.div
              className="font-sans text-paragraph-large text-white mb-12"
              variants={fadeIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              {data.galleryLabel}
            </m.div>
            <div
              className={`grid ${
                data.buttons.length > 1
                  ? "grid-cols-2 gap-x-8 gap-y-4 w-full mt-4 justify-center"
                  : "w-fit"
              }`}
            >
              {data.buttons.map((btn, i) => (
                <m.div
                  key={btn.item.title}
                  variants={fadeIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  className="flex justify-center"
                >
                  <Button
                    title={btn.item.title}
                    type={btn.item.type}
                    link={btn.item.link}
                    className="w-full items-center justify-center flex"
                  />
                </m.div>
              ))}
            </div>
          </div>

          {/* Images Section - 55% */}
          <div
            className="flex flex-row items-end justify-center pt-4"
            style={{
              flexBasis: "55%",
              flexGrow: 0,
              flexShrink: 0,
              maxWidth: "55%",
            }}
          >
            {[beforeImageSrc, afterImageSrc].map((img, idx) => (
              <m.div
                key={idx}
                variants={fadeIn}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                className="flex flex-col items-center"
                style={{
                  width: "50%",
                  maxWidth: "50%",
                  paddingLeft: idx === 1 ? "3.33%" : 0,
                }}
              >
                <Image
                  src={img}
                  alt={idx === 0 ? "Before" : "After"}
                  width={340}
                  height={420}
                  className="object-cover object-bottom mb-2 w-full h-[420px] rounded-none"
                  style={{ maxWidth: "100%" }}
                />
                <span className="font-sans text-accent-color-two text-md tracking-widest mt-2">
                  {idx === 0 ? "BEFORE" : "AFTER"}
                </span>
              </m.div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </LazyMotion>
  );
}
